import { isNumber } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import {
    BaseStagePlugin,
    getStageIndexByMousePointY,
    useActiveOriginIndex,
    useFramework,
    useIsLongImageMode,
} from '@/framework';
import { RealEdit } from '..';
import { ICollaboratorInfo } from '../types';

/**
 * 渲染用户光标
 */
export const RenderUserCursor: React.FC = React.memo(() => {
    const framework = useFramework();
    const realEdit = framework.getInstance(RealEdit);
    const activeOriginIndex = useActiveOriginIndex();
    const isLongImageMode = useIsLongImageMode();
    // 监听鼠标移动同步发送
    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            const needSync = realEdit.room.isRealEdit();
            if (!needSync) return;
            const stageIndex = getStageIndexByMousePointY(framework.identity, e.y);
            realEdit.moveCursor.send(stageIndex, e.x, e.y);
        };
        document.addEventListener('mousemove', onMouseMove);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
        };
    }, []);

    const [userCursor, setUserCursor] = useState<ICollaboratorInfo[]>([]);

    // 监听用户光标数据
    useEffect(() => {
        const handler = realEdit.room.onUserCursor((members) => {
            members.forEach((member) => {
                framework.events.emit(`user-cursor-${member.wsKey}`, member.x, member.y, member.originIndex);
            });
            setUserCursor(members);
        });
        return () => {
            framework.events.off(handler);
        };
    }, []);

    return (
        <>
            {userCursor.map((user) => {
                return (
                    <UserCursor
                        key={user.wsKey}
                        isActiveOriginIndex={isLongImageMode || activeOriginIndex === user.originIndex}
                        screenName={user.screen_name}
                        wsKey={user.wsKey}
                        originIndex={user.originIndex}
                        color={user.color}
                        realEdit={realEdit}
                    />
                );
            })}
        </>
    );
});

const UserCursor: React.FC<{
    realEdit: RealEdit;
    wsKey: number;
    color: string;
    originIndex: number | null;
    screenName: string;
    isActiveOriginIndex: boolean;
}> = React.memo(({ realEdit, wsKey, originIndex, screenName, color, isActiveOriginIndex }) => {
    const framework = useFramework();
    const [show, setShow] = useState(false);
    const nodeRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = framework.events.on(`user-cursor-${wsKey}`, (x, y, originIndex) => {
            if (isNumber(x) && isNumber(y)) {
                if (nodeRef.current) {
                    const { width, height } = framework.tryGetStageTransform(
                        originIndex,
                        framework.stage.getScale(),
                        false,
                    );
                    nodeRef.current.style.opacity = '1';
                    nodeRef.current.style.transform = `translate(${x * width}px, ${y * height}px)`;
                }
                setShow(realEdit.moveCursor.checkNeedRender(x, y));
            } else {
                setShow(false);
            }
        });
        return () => {
            framework.events.off(handler);
        };
    }, [wsKey]);

    if (!isActiveOriginIndex || !isNumber(originIndex)) return null;

    return (
        <BaseStagePlugin
            style={{ overflow: 'initial', zIndex: 20, opacity: show ? 1 : 0 }}
            originIndexOrLayerId={originIndex}
            name="ot-user-cursor"
        >
            <div ref={nodeRef} className="real-edit__user-cursor" style={{ background: color, opacity: 0 }}>
                {show ? (
                    <>
                        <svg width="53" height="53" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M17.3627 19.8014C17.0888 19.8751 16.8749 20.089 16.8013 20.3628L15.9166 23.6521C15.7096 24.422 14.6247 24.4422 14.389 23.6806L12.0162 16.0111C11.8272 15.4002 12.4 14.8274 13.0109 15.0164L20.6805 17.3892C21.4421 17.6248 21.4218 18.7098 20.652 18.9168L17.3627 19.8014Z"
                                fill={color}
                            />
                            <path
                                d="M16.4001 23.7821C16.0629 25.0358 14.2955 25.0689 13.9114 23.829L11.5387 16.1586L11.4952 15.9728C11.3579 15.1132 12.1133 14.3576 12.9729 14.4951L13.1587 14.5386L20.8284 16.9119C22.0685 17.2959 22.0358 19.0626 20.7822 19.3999L17.4925 20.2838L17.4207 20.3142C17.3764 20.34 17.3393 20.3769 17.3136 20.4212L17.2839 20.4924L16.4001 23.7821Z"
                                stroke="white"
                            />
                        </svg>
                        {screenName ? (
                            <div className="real-edit__user-cursor-inner" style={{ background: color }}>
                                {screenName}
                            </div>
                        ) : null}
                    </>
                ) : null}
            </div>
        </BaseStagePlugin>
    );
});
